import { plotUnlockCost, type GameAction } from './actions';
import { THIRSTY_THRESHOLD } from './constants';
import { FLOWER_IDS, FLOWERS, type FlowerId } from './flowers';
import { seedCount, type GameState } from './state';

export const TOOLS = ['hand', 'water', 'seeds', 'shovel'] as const;
export type Tool = (typeof TOOLS)[number];

export type IntentTone = 'harvest' | 'plant' | 'water' | 'buy' | 'dig' | 'info' | 'blocked';
export type ToolAnimation = 'harvest' | 'plant' | 'water' | 'dig' | 'none';

export interface PlotIntent {
  /** The action to dispatch, or null when clicking only shows information. */
  action: GameAction | null;
  label: string;
  tone: IntentTone;
  animation: ToolAnimation;
}

const info = (label: string): PlotIntent => ({
  action: null,
  label,
  tone: 'info',
  animation: 'none',
});
const blocked = (label: string): PlotIntent => ({
  action: null,
  label,
  tone: 'blocked',
  animation: 'none',
});

/** The seed to plant: the selected one if we have it, otherwise the first we do have. */
export function resolveSeed(state: GameState, preferred: FlowerId | null): FlowerId | null {
  if (preferred && seedCount(state, preferred) > 0) return preferred;
  return FLOWER_IDS.find((id) => seedCount(state, id) > 0) ?? null;
}

function plantIntent(state: GameState, plotId: number, seed: FlowerId | null): PlotIntent {
  const species = resolveSeed(state, seed);
  if (!species) return blocked('No seeds — visit the market stand 🏪');
  return {
    action: { type: 'plant', plotId, species },
    label: `Plant ${FLOWERS[species].name} ${FLOWERS[species].emoji}`,
    tone: 'plant',
    animation: 'plant',
  };
}

function waterIntent(plotId: number): PlotIntent {
  return {
    action: { type: 'water', plotId },
    label: 'Water 💧',
    tone: 'water',
    animation: 'water',
  };
}

/**
 * Decides what a click on a plot should do for the selected tool. Shared by the
 * hover tooltip and the click handler so they can never disagree.
 */
export function resolvePlotIntent(
  state: GameState,
  plotId: number,
  tool: Tool,
  selectedSeed: FlowerId | null,
): PlotIntent {
  const plot = state.plots[plotId];
  if (!plot) return info('');

  if (!plot.unlocked) {
    const cost = plotUnlockCost(state);
    if (state.coins < cost) return blocked(`For sale · 🪙${cost}`);
    return {
      action: { type: 'unlockPlot', plotId },
      label: `Buy this plot · 🪙${cost}`,
      tone: 'buy',
      animation: 'dig',
    };
  }

  const crop = plot.crop;
  const def = crop ? FLOWERS[crop.species] : null;
  const progress = crop ? `${Math.floor(crop.growth * 100)}%` : '';

  switch (tool) {
    case 'shovel':
      if (!crop || !def) return info('Empty soil');
      return {
        action: { type: 'dig', plotId },
        label: `Dig up ${def.name}`,
        tone: 'dig',
        animation: 'dig',
      };

    case 'water':
      return waterIntent(plotId);

    case 'seeds':
      if (!crop) return plantIntent(state, plotId, selectedSeed);
      break;

    case 'hand':
      break;
  }

  // Smart hand behaviour (also the fallback for the seed tool on planted plots).
  if (!crop || !def) return plantIntent(state, plotId, selectedSeed);
  if (crop.growth >= 1) {
    return {
      action: { type: 'harvest', plotId },
      label: `Harvest ${crop.golden ? '✨Golden ' : ''}${def.name} ${def.emoji}`,
      tone: 'harvest',
      animation: 'harvest',
    };
  }
  if (plot.moisture < THIRSTY_THRESHOLD) return waterIntent(plotId);
  return info(`${def.emoji} ${def.name} · growing ${progress}`);
}
